import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Glossary } from "src/components/Text/Glossary";
import { StudentSection } from "./StudentSection";
import {
  ParentAddress,
  useAddStudentsToParent,
} from "./useAddStudentsToParent";
import { useStudentEntries } from "./useStudentEntries";

interface AddStudentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  parentId: string;
  parentName: string;
  parentAddress: ParentAddress;
  // Student ids already linked to the parent, excluded from search.
  excludeIds: string[];
}

export function AddStudentsDialog({
  isOpen,
  onClose,
  organizationId,
  parentId,
  parentName,
  parentAddress,
  excludeIds,
}: AddStudentsDialogProps) {
  const students = useStudentEntries();
  const { submit, isSubmitting } = useAddStudentsToParent({
    organizationId,
    parentId,
    parentAddress,
  });

  const handleAdd = async () => {
    amplitude.track("Add relationship clicked");
    const added = await submit(students.entries);
    if (added) onClose();
  };

  // An open (unconfirmed) new student sub-form must be added or cancelled first.
  const hasOpenStudentDraft = students.entries.some(
    (entry) => entry.kind === "new" && !entry.confirmed
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Glossary>Add student</Glossary>
        </ModalHeader>
        <ModalBody>
          <StudentSection
            organizationId={organizationId}
            entries={students.entries}
            parentName={parentName}
            excludeIds={excludeIds}
            onAddExisting={students.addExisting}
            onAddDraft={students.addDraft}
            onUpdateDraft={students.updateDraft}
            onConfirmDraft={students.confirmDraft}
            onRemove={students.remove}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            type="button"
            variant="ghost"
            colorScheme="gray"
            onClick={() => {
              amplitude.track("Cancel relationship clicked");
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            isDisabled={students.entries.length === 0 || hasOpenStudentDraft}
            isLoading={isSubmitting}
            onClick={handleAdd}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
